import type { ParseKeys } from 'i18next'

export type TranslationKey = ParseKeys<'common'>

interface ApiError {
	response?: {
		status?: number
		data?: { message?: string }
	}
}

export type ApiErrorDisplay =
	| { kind: 'translated'; key: TranslationKey }
	| { kind: 'verbatim'; message: string }

/** Nexus sends no error codes, only English prose, so the conditions worth explaining are matched by text. */
const MESSAGE_KEYS: ReadonlyArray<{ pattern: RegExp; key: TranslationKey }> = [
	{ pattern: /once in 24 hours/i, key: 'whatsappSettings.templateEditCooldown' },
	{ pattern: /pending Meta review/i, key: 'whatsappSettings.templateEditPending' },
	{ pattern: /WhatsApp is not configured/i, key: 'whatsappSettings.whatsappNotConfigured' },
]

function asApiError(error: unknown): ApiError {
	return (error !== null && typeof error === 'object' ? error : {}) as ApiError
}

export function apiErrorMessage(error: unknown): string | undefined {
	return asApiError(error).response?.data?.message
}

export function isUserActionableError(error: unknown): boolean {
	const status = asApiError(error).response?.status

	return status !== undefined && status >= 400 && status < 500
}

/** Unmatched 4xx messages are shown verbatim: they name the actual problem (validation rule, Meta rejection); the fallback doesn't. */
export function apiErrorDisplay(error: unknown, fallbackKey: TranslationKey): ApiErrorDisplay {
	const message = apiErrorMessage(error)
	const matched = message === undefined
		? undefined
		: MESSAGE_KEYS.find(({ pattern }) => pattern.test(message))

	if (matched) { return { kind: 'translated', key: matched.key } }
	if (message && isUserActionableError(error)) { return { kind: 'verbatim', message } }

	return { kind: 'translated', key: fallbackKey }
}
